import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Calendar, 
  Clock, 
  MapPin, 
  Star, 
  Loader, 
  Search, 
  Filter, 
  CheckCircle, 
  XCircle, 
  AlertCircle,
  Mail,
  Ban,
  User
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useNotification } from '../../context/NotificationContext';

interface Booking {
  id: string;
  reservation_id: string;
  booking_date: string;
  status: string;
  address: string;
  notes: string;
  services: {
    name: string;
    price: number;
    duration: number;
  };
  profiles: {
    full_name: string | null;
  };
  user_email?: string;
}

type StatusFilter = 'all' | 'upcoming' | 'completed' | 'cancelled';

export default function Bookings() {
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const [loading, setLoading] = useState(true);
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('upcoming');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedBookings, setSelectedBookings] = useState<Set<string>>(new Set());
  const [processingAction, setProcessingAction] = useState<string | null>(null);
  const [confirmationDialog, setConfirmationDialog] = useState<{
    isOpen: boolean;
    bookingId: string;
    action: 'confirm' | 'cancel';
  } | null>(null);

  useEffect(() => {
    fetchBookings();
  }, []);

  const fetchBookings = async () => {
    try {
      setLoading(true);
      
      // Get the current session
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        navigate('/admin/login');
        return;
      }

      // Call the admin-bookings edge function
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-bookings`, {
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to fetch bookings');
      }

      const data = await response.json();
      setBookings(data);
    } catch (error) {
      console.error('Error fetching bookings:', error);
      showNotification('error', 'Failed to load bookings');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (bookingId: string, newStatus: string) => {
    setProcessingAction(bookingId);
    try {
      // Get the current session
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('No session found');
      }

      // Call the admin-bookings edge function to update status
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-bookings/${bookingId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ status: newStatus }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to update booking status');
      }

      setBookings(bookings.map(booking =>
        booking.id === bookingId
          ? { ...booking, status: newStatus }
          : booking
      ));

      const actionText = newStatus === 'confirmed' ? 'confirmed' : 'cancelled';
      showNotification('success', `Booking has been ${actionText} successfully`);
      
      // Send notification to customer
      const booking = bookings.find(b => b.id === bookingId);
      if (booking) {
        showNotification('info', `Notification sent to ${booking.user_email}`);
      }
    } catch (error) {
      console.error('Error updating booking status:', error);
      showNotification('error', 'Failed to update booking status');
    } finally {
      setProcessingAction(null);
      setConfirmationDialog(null);
    }
  };

  const handleContactCustomer = async (email: string) => {
    try {
      showNotification('success', `Opening email client for ${email}`);
      window.location.href = `mailto:${email}?subject=Regarding your booking with Aussi Clean`;
    } catch (error) {
      showNotification('error', 'Failed to open email client');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'confirmed':
        return 'text-green-600 bg-green-100 dark:bg-green-900/50 dark:text-green-400';
      case 'pending':
        return 'text-yellow-600 bg-yellow-100 dark:bg-yellow-900/50 dark:text-yellow-400';
      case 'cancelled':
        return 'text-red-600 bg-red-100 dark:bg-red-900/50 dark:text-red-400';
      case 'completed':
        return 'text-blue-600 bg-blue-100 dark:bg-blue-900/50 dark:text-blue-400';
      default:
        return 'text-gray-600 bg-gray-100 dark:bg-gray-700 dark:text-gray-400';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'confirmed':
        return <CheckCircle className="w-5 h-5" />;
      case 'pending':
        return <AlertCircle className="w-5 h-5" />;
      case 'cancelled':
        return <XCircle className="w-5 h-5" />;
      case 'completed':
        return <CheckCircle className="w-5 h-5" />;
      default:
        return <AlertCircle className="w-5 h-5" />;
    }
  };

  const filteredBookings = bookings.filter(booking => {
    const matchesStatus = 
      statusFilter === 'all' ? true :
      statusFilter === 'upcoming' ? ['confirmed', 'pending'].includes(booking.status) :
      booking.status === statusFilter;

    const matchesSearch = 
      booking.services?.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      booking.address.toLowerCase().includes(searchQuery.toLowerCase()) ||
      booking.reservation_id.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (booking.user_email?.toLowerCase() || '').includes(searchQuery.toLowerCase()) ||
      (booking.profiles?.full_name?.toLowerCase() || '').includes(searchQuery.toLowerCase());

    return matchesStatus && matchesSearch;
  });

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-2xl font-bold">Bookings</h1>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 mb-6">
        <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
          <div className="flex gap-2">
            {[
              { value: 'all', label: 'All Bookings' },
              { value: 'upcoming', label: 'Upcoming' },
              { value: 'completed', label: 'Completed' },
              { value: 'cancelled', label: 'Cancelled' },
            ].map((filter) => (
              <button
                key={filter.value}
                onClick={() => setStatusFilter(filter.value as StatusFilter)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  statusFilter === filter.value
                    ? 'bg-blue-50 dark:bg-blue-900/50 text-blue-600 dark:text-blue-400'
                    : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                }`}
              >
                {filter.label}
              </button>
            ))}
          </div>
          <div className="relative w-full md:w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search bookings..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
            />
          </div>
        </div>
      </div>

      <div className="space-y-6">
        {filteredBookings.length === 0 ? (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-8 text-center">
            <Calendar className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">No bookings found</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              {searchQuery
                ? "No bookings match your search criteria"
                : "There are no bookings in the system yet"}
            </p>
          </div>
        ) : (
          filteredBookings.map((booking) => (
            <div
              key={booking.id}
              className="bg-white dark:bg-gray-800 rounded-xl shadow-sm overflow-hidden border border-gray-200 dark:border-gray-700"
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex flex-col gap-2">
                    <div className="flex items-center gap-3">
                      <div className="flex flex-col">
                        <span
                          className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium w-fit ${getStatusColor(
                            booking.status
                          )}`}
                        >
                          {getStatusIcon(booking.status)}
                          {booking.status.charAt(0).toUpperCase() + booking.status.slice(1)}
                        </span>
                        <span className="text-sm text-gray-500 dark:text-gray-400 font-mono mt-1">
                          {booking.reservation_id}
                        </span>
                      </div>
                      <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                        {booking.services?.name || 'Unknown Service'}
                      </h3>
                    </div>
                  </div>
                  <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                    ${((booking.services?.price || 0) / 100).toFixed(2)}
                  </p>
                </div>

                <div className="mb-6">
                  <div className="flex items-center gap-2 text-gray-700 dark:text-gray-300 mb-2">
                    <Calendar className="w-5 h-5 flex-shrink-0" />
                    <span>
                      {new Date(booking.booking_date).toLocaleDateString('en-US', {
                        weekday: 'long',
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                      })}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                    <Clock className="w-5 h-5 flex-shrink-0" />
                    <span>
                      {new Date(booking.booking_date).toLocaleTimeString('en-US', {
                        hour: 'numeric',
                        minute: 'numeric',
                      })}
                      <span className="text-gray-400 dark:text-gray-500 mx-2">·</span>
                      {booking.services?.duration || 0} minutes
                    </span>
                  </div>
                </div>

                {booking.notes && (
                  <div className="mb-6 border-y dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50 -mx-6 px-6 py-4">
                    <p className="text-gray-700 dark:text-gray-300">{booking.notes}</p>
                  </div>
                )}

                <div className="mb-6 space-y-3 bg-blue-50 dark:bg-blue-900/20 -mx-6 px-6 py-4 border-y dark:border-gray-700">
                  <div className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
                    <User className="w-5 h-5 flex-shrink-0 text-blue-500 dark:text-blue-400" />
                    <span className="font-medium">{booking.profiles?.full_name || 'No name'}</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
                    <Mail className="w-5 h-5 flex-shrink-0 text-blue-500 dark:text-blue-400" />
                    <span>{booking.user_email || 'Unknown email'}</span>
                  </div>
                  <div className="flex items-start gap-2 text-gray-700 dark:text-gray-300">
                    <MapPin className="w-5 h-5 flex-shrink-0 mt-1 text-blue-500 dark:text-blue-400" />
                    <span>{booking.address}</span>
                  </div>
                </div>

                <div className="flex justify-end gap-2">
                  {booking.status === 'pending' && (
                    <>
                      <button
                        onClick={() => setConfirmationDialog({
                          isOpen: true,
                          bookingId: booking.id,
                          action: 'confirm'
                        })}
                        disabled={processingAction === booking.id}
                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2 disabled:opacity-50"
                      >
                        <CheckCircle className="w-4 h-4" />
                        Confirm
                      </button>
                      <button
                        onClick={() => setConfirmationDialog({
                          isOpen: true,
                          bookingId: booking.id,
                          action: 'cancel'
                        })}
                        disabled={processingAction === booking.id}
                        className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2 disabled:opacity-50"
                      >
                        <Ban className="w-4 h-4" />
                        Cancel
                      </button>
                    </>
                  )}
                  <button
                    onClick={() => handleContactCustomer(booking.user_email || '')}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                  >
                    <Mail className="w-4 h-4" />
                    Contact
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Confirmation Dialog */}
      {confirmationDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md mx-4">
            <h3 className="text-lg font-semibold mb-4">
              {confirmationDialog.action === 'confirm'
                ? 'Confirm Booking'
                : 'Cancel Booking'}
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              {confirmationDialog.action === 'confirm'
                ? 'Are you sure you want to confirm this booking? An email will be sent to notify the customer.'
                : 'Are you sure you want to cancel this booking? This action cannot be undone.'}
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setConfirmationDialog(null)}
                className="px-4  py-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100"
              >
                No, go back
              </button>
              <button
                onClick={() => handleStatusChange(
                  confirmationDialog.bookingId,
                  confirmationDialog.action === 'confirm' ? 'confirmed' : 'cancelled'
                )}
                disabled={processingAction === confirmationDialog.bookingId}
                className={`px-4 py-2 text-white rounded-lg flex items-center gap-2 ${
                  confirmationDialog.action === 'confirm'
                    ? 'bg-green-600 hover:bg-green-700'
                    : 'bg-red-600 hover:bg-red-700'
                }`}
              >
                {processingAction === confirmationDialog.bookingId ? (
                  <>
                    <Loader className="w-4 h-4 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    {confirmationDialog.action === 'confirm' ? (
                      <CheckCircle className="w-4 h-4" />
                    ) : (
                      <Ban className="w-4 h-4" />
                    )}
                    Yes, {confirmationDialog.action === 'confirm' ? 'confirm' : 'cancel'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}